import { Platform } from 'react-native';
const RNFS = Platform.OS !== 'web' ? require('react-native-fs') : null;
import { offlineDatabase, PhotoQueueItem } from './offlineDatabase';
import { reviewPhotoApi } from './reviewPhotoApi';
import NetInfo from '@react-native-community/netinfo';
import {
  applyDetoxNetworkOverride,
  getEffectiveNetworkOnline,
  subscribeToDetoxNetworkOverride,
} from '../utils/detoxTestControls';
import { selectBestResponseForItem } from './reviewResponseSelector';
import { shouldUseOfflineReviewWorkflow } from '../config/offlineWorkflow';
import { getOfflineEnvironmentKey } from './offlineEnvironment';

/**
 * Extended photo queue item for local storage
 */
export interface LocalPhotoQueueItem extends PhotoQueueItem {
  // Additional fields for queue management
  isHandwriting?: boolean; // For Claude Vision processing
  presignedUrlExpiry?: Date; // Track S3 presigned URL expiration
  originalUri?: string; // Original URI for handwriting images
  processedForVision?: boolean; // Track if submitted to Claude Vision
}

/**
 * Upload options for photo queue
 */
export interface PhotoUploadOptions {
  reviewId: number;
  responseId: number;
  responseTempId?: string;
  itemId: number;
  questionId?: string;
  caption?: string;
  isHandwriting?: boolean;
  priority?: 'low' | 'normal' | 'high';
}

export interface UploadedPhotoNoteMetadata {
  responseId: number;
  s3Key?: string;
  s3Url?: string;
  fileName: string;
  mimeType: string;
  size?: number;
  caption?: string;
  metadata?: Record<string, unknown>;
  questionId?: string;
  uploadedAt?: Date | string;
  queueId?: number;
}

export const LOCAL_PHOTO_NOTE_UPDATED_AT_KEY = 'localNoteUpdatedAt';

/**
 * Batch upload result
 */
export interface BatchUploadResult {
  successful: PhotoQueueItem[];
  failed: PhotoQueueItem[];
  totalProcessed: number;
}

export interface PhotoQueueRepairResult {
  verified: number;
  pathRepaired: number;
  rehydrated: number;
  missing: number;
  missingPhotoIds: number[];
}

export interface LocalPhotoFileStats {
  available: number;
  missing: number;
}

export interface ForcedPhotoRetryOptions {
  photoIds?: number[];
  reviewResponseIds?: number[];
  responseTempIds?: string[];
}

export interface ForcedPhotoRetryResult extends BatchUploadResult {
  reset: number;
  missing: number;
  stillFailed: number;
}

export const PHOTO_FILE_MISSING_ERROR = 'Photo file not found on this device';
const PHOTO_FILE_UNVERIFIED_ERROR = 'Unable to verify photo file on this device';
const PHOTO_STORAGE_STAGING_ERROR = 'Photo storage preparation is incomplete';
const PHOTO_ROLLBACK_COPY_ERROR =
  'Photo rollback copy could not be prepared';
const PHOTO_STORAGE_STATE_STAGING = 'staging';
const PHOTO_STORAGE_STATE_COPYING = 'copying';
const PHOTO_STORAGE_STATE_READY = 'ready';

class RollbackPathConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RollbackPathConflictError';
  }
}

const localPathFromUri = (uri: unknown): string => {
  if (typeof uri !== 'string') {
    return '';
  }

  return uri.startsWith('file://') ? uri.slice('file://'.length) : uri;
};

const isRegularFile = (stats: any): boolean =>
  !(typeof stats.isDirectory === 'function' && stats.isDirectory()) &&
  !(typeof stats.isFile === 'function' && !stats.isFile());

const hashPhotoQueueEnvironmentKey = (environmentKey: string): string => {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < environmentKey.length; index += 1) {
    const codeUnit = environmentKey.charCodeAt(index);
    first = Math.imul(first ^ codeUnit, 0x01000193);
    second = Math.imul(second ^ (codeUnit + index), 0x27d4eb2d);
  }
  return [first, second]
    .map(hash => (hash >>> 0).toString(16).padStart(8, '0'))
    .join('');
};

/** Bounded, URI-safe, collision-resistant directory component. */
export const encodePhotoQueueEnvironmentKey = (environmentKey: string): string => {
  if (!environmentKey) {
    throw new Error('Photo queue environment key is required');
  }

  const slug = environmentKey
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'environment';

  return `env-${slug}-${hashPhotoQueueEnvironmentKey(environmentKey)}`;
};

/**
 * Photo queue service for managing offline photo uploads with local storage
 * Features:
 * - Store photos locally using react-native-fs
 * - Progressive upload queue with retry logic
 * - Handle S3 presigned URL expiration
 * - Support handwriting images for Claude Vision
 * - Batch upload capabilities
 * - Cleanup after successful uploads
 */
class PhotoQueueService {
  private activeEnvironmentKey: string | null = null;

  private get LEGACY_STORAGE_DIR() {
    return `${RNFS.DocumentDirectoryPath}/photos_queue`;
  }

  private get V2_STORAGE_ROOT() {
    return `${RNFS.DocumentDirectoryPath}/photos_queue_v2`;
  }

  private get LOCAL_STORAGE_DIR() {
    return `${this.V2_STORAGE_ROOT}/${encodePhotoQueueEnvironmentKey(
      this.captureEnvironmentKey()
    )}`;
  }
  private readonly MAX_RETRIES = 3;
  private readonly BATCH_SIZE = 5;
  private readonly RETRY_DELAYS = [1000, 5000, 15000]; // Exponential backoff in ms
  private uploadInProgress = false;
  private uploadRerunRequested = false;
  private initialized = false;
  private lifecycleGeneration = 0;
  private initializationPromise: Promise<void> | null = null;
  private destructionPromise: Promise<void> | null = null;
  private networkListener: (() => void) | null = null;
  private activeStagingQueueIds = new Set<number>();
  private uploadCompletionCallbacks: ((queueId: number, backendId?: number) => void)[] = [];
  private lastRepairResult: PhotoQueueRepairResult = {
    verified: 0,
    pathRepaired: 0,
    rehydrated: 0,
    missing: 0,
    missingPhotoIds: [],
  };

  private captureEnvironmentKey(): string {
    const currentEnvironmentKey = getOfflineEnvironmentKey();
    if (this.activeEnvironmentKey && this.activeEnvironmentKey !== currentEnvironmentKey) {
      throw new Error(
        'Photo queue environment changed; destroy and reinitialize the service before continuing'
      );
    }
    this.activeEnvironmentKey ||= currentEnvironmentKey;
    return this.activeEnvironmentKey;
  }

  /**
   * Initialize the photo queue service
   */
  async initialize(): Promise<void> {
    if (this.destructionPromise) {
      await this.destructionPromise;
    }

    const initializationEnvironment = this.captureEnvironmentKey();
    if (this.initialized) {
      return;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    const initializationGeneration = this.lifecycleGeneration;
    const initializationPromise = (async () => {
      // Initialize offline database
      await offlineDatabase.initialize();
      this.assertLifecycleGeneration(initializationGeneration);
      this.captureEnvironmentKey();

      // Ensure local storage directory exists
      await this.ensureStorageDirectory();
      this.assertLifecycleGeneration(initializationGeneration);

      // Repair persisted absolute paths before cleanup or network-triggered uploads.
      this.lastRepairResult = await this.repairStoredQueuePaths();
      this.assertLifecycleGeneration(initializationGeneration);

      // Clean up old files and expired entries
      await this.cleanupExpiredItems();
      this.assertLifecycleGeneration(initializationGeneration);
      this.captureEnvironmentKey();

      // Start network monitoring only after every recoverable local file is protected.
      this.startNetworkMonitoring(initializationEnvironment);
      this.initialized = true;
    })();
    this.initializationPromise = initializationPromise;

    try {
      await initializationPromise;
    } catch (error) {
      if (this.initializationPromise === initializationPromise) {
        this.initializationPromise = null;
      }
      console.warn('[PhotoQueueService] Failed to initialize:', error);
      throw new Error(`PhotoQueueService initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Add photo to upload queue
   */
  async addToQueue(
    localUri: string,
    options: PhotoUploadOptions
  ): Promise<PhotoQueueItem> {
    let activeStagingQueueId: number | null = null;
    try {
      await this.initialize();
      this.captureEnvironmentKey();

      // Safety net: ensure we have valid response identification
      if (options.responseId <= 0 && !options.responseTempId) {
        console.error('[PhotoQueueService] ❌ INVALID: Cannot queue photo without valid responseId or responseTempId', {
          responseId: options.responseId,
          responseTempId: options.responseTempId,
          itemId: options.itemId,
          reviewId: options.reviewId
        });
        throw new Error('Cannot queue photo: no valid response ID or temp ID provided');
      }

      if (typeof localUri !== 'string' || localUri.length === 0) {
        throw new Error('Cannot queue photo without a local URI');
      }

      // Normalize URI (handle file:// and content:// sources)
      const isContentUri = localUri.startsWith('content://');
      const normalizedSource = localPathFromUri(localUri);
      let expectedSourceSize = 0;
      let expectedSourceChecksum: string | null = null;
      try {
        const sourceStats = await RNFS.stat(normalizedSource);
        const sourceSize = Number(sourceStats.size);
        if (isRegularFile(sourceStats) && Number.isFinite(sourceSize) && sourceSize > 0) {
          expectedSourceSize = sourceSize;
        }
      } catch {
        // Some providers cannot be statted before copying. Their complete size
        // is checkpointed immediately after the confined copy instead.
      }
      try {
        expectedSourceChecksum =
          await this.getQueueFileDigest(normalizedSource);
      } catch {
        // Provider URIs are not always hashable. The confined copy still gets
        // a durable checksum before it can be published.
      }

      let fileName = '';
      let storagePath = '';
      let legacyPath = '';
      let legacyPendingPath = '';
      let copyingPath = '';
      let readyPath = '';
      const environmentFilePrefix =
        hashPhotoQueueEnvironmentKey(this.captureEnvironmentKey());
      for (let attempt = 0; attempt < 10; attempt += 1) {
        const candidateFileName =
          `${environmentFilePrefix}_${Date.now()}_` +
          `${Math.random().toString(36).slice(2, 12)}` +
          `${Math.random().toString(36).slice(2, 12)}_${attempt}.jpg`;
        const candidateStoragePath =
          this.getCanonicalStoragePath({ fileName: candidateFileName });
        const candidateLegacyPath =
          this.getLegacyStoragePath({ fileName: candidateFileName });
        const candidateLegacyPendingPath =
          this.getLegacyPendingPath({ fileName: candidateFileName });
        const candidateCopyingPath =
          this.getStagingFilePath(candidateFileName, 'copying');
        const candidateReadyPath =
          this.getStagingFilePath(candidateFileName, 'ready');
        if (
          !candidateStoragePath ||
          !candidateLegacyPath ||
          !candidateLegacyPendingPath ||
          !candidateCopyingPath ||
          !candidateReadyPath ||
          await RNFS.exists(candidateStoragePath) ||
          await RNFS.exists(candidateLegacyPath) ||
          await RNFS.exists(candidateLegacyPendingPath) ||
          await RNFS.exists(candidateCopyingPath) ||
          await RNFS.exists(candidateReadyPath)
        ) {
          continue;
        }
        fileName = candidateFileName;
        storagePath = candidateStoragePath;
        legacyPath = candidateLegacyPath;
        legacyPendingPath = candidateLegacyPendingPath;
        copyingPath = candidateCopyingPath;
        readyPath = candidateReadyPath;
        break;
      }
      if (
        !fileName ||
        !storagePath ||
        !legacyPath ||
        !legacyPendingPath ||
        !copyingPath ||
        !readyPath
      ) {
        throw new Error(
          'Unable to allocate collision-free queued photo storage'
        );
      }

      // Persist the recovery source before copying bytes. If the app exits during
      // publication, startup can rehydrate this row from metadata.originalUri.
      // A database failure therefore cannot strand an unreferenced queue file.
      const queueItem: Omit<PhotoQueueItem, 'id' | 'createdAt'> = {
        // Released v1 sees an absent sentinel while copying is incomplete. It
        // rewrites this pointer to its canonical filename only after its own
        // copy completes, giving v2 a cross-version completion marker.
        localPath: legacyPendingPath,
        reviewResponseId: options.responseId,
        tempId: options.responseTempId,
        fileName,
        mimeType: 'image/jpeg',
        size: expectedSourceSize,
        caption: options.caption,
        metadata: {
          reviewId: options.reviewId,
          itemId: options.itemId,
          questionId: options.questionId,
          isHandwriting: options.isHandwriting,
          priority: options.priority || 'normal',
          originalUri: localUri,
          storageState: PHOTO_STORAGE_STATE_COPYING,
          rollbackFileAllocated: true,
          ...(expectedSourceChecksum
            ? { storageChecksum: expectedSourceChecksum }
            : {}),
        },
        status: 'failed',
        retryCount: this.MAX_RETRIES,
        errorMessage: PHOTO_STORAGE_STAGING_ERROR,
      };
      const savedItem = await offlineDatabase.addPhotoToQueue(queueItem);
      if (!savedItem.id) {
        throw new Error('Queued photo staging row is missing its durable ID');
      }
      activeStagingQueueId = savedItem.id;
      this.activeStagingQueueIds.add(savedItem.id);

      const readyPublication = await this.publishEphemeralSourceToReady(
        savedItem,
        normalizedSource,
        {
          originalUri: localUri,
          isContentUri,
        }
      );
      const preparedSize = readyPublication.size;

      const preparedPath = await this.publishQueueFileToLegacy(
        savedItem,
        readyPublication.path,
        { acceptExistingCanonical: false }
      );

      // Publish v2 only from the immutable v1-layout source. The legacy copy is
      // retained throughout the expand release so an installed v1 binary can
      // recover this row after the app data-container UUID changes.
      await this.publishQueueFileToV2(
        { id: savedItem.id, fileName, size: preparedSize },
        preparedPath,
        { acceptExistingCanonical: false }
      );

      // Get file info
      const fileInfo = await RNFS.stat(storagePath);
      const mutationId = await offlineDatabase.recordOfflineMutation?.({
        entityType: 'photo',
        entityId: savedItem.id ?? null,
        reviewId: options.reviewId,
        itemId: options.itemId,
        mutationType: 'photo_queue_add',
        payload: {
          responseId: options.responseId,
          responseTempId: options.responseTempId,
          questionId: options.questionId,
          fileName,
        },
        status: 'pending',
      });
      await offlineDatabase.createRecoverySnapshot?.(options.reviewId, 'photo_queue_add');
      const currentReview = await offlineDatabase.getReview?.(options.reviewId).catch(() => null);
      await offlineDatabase.updateReview?.(options.reviewId, {
        syncStatus: 'pending',
        lastLocalMutationAt: new Date(),
        localRevision: (currentReview?.localRevision ?? 0) + 1,
        conflictStatus: 'none',
      }).catch(error => {
        console.warn('[PhotoQueueService] Failed to mark review pending for queued photo:', error);
      });

      const committedMetadata = {
        ...savedItem.metadata,
        ...(mutationId ? { offlineMutationId: mutationId } : {}),
      };
      delete committedMetadata.storageState;
      delete committedMetadata.rollbackFileAllocated;
      if (savedItem.id) {
        // This single update is the publication boundary. Upload selectors only
        // see the item after both rollback/v2 bytes and the mutation are durable.
        await offlineDatabase.updatePhotoQueueItem(savedItem.id, {
          localPath: storagePath,
          size: fileInfo.size,
          status: 'pending',
          retryCount: 0,
          errorMessage: null,
          metadata: committedMetadata,
        });
      }
      savedItem.localPath = storagePath;
      savedItem.size = fileInfo.size;
      savedItem.status = 'pending';
      savedItem.retryCount = 0;
      savedItem.errorMessage = null;
      savedItem.metadata = committedMetadata;

      // Web keeps its existing immediate-upload behavior. Native uploads run
      // through the review full-sync pipeline so response IDs are resolved first.
      if (!shouldUseOfflineReviewWorkflow() && await this.isOnline()) {
        this.processUploadQueue();
      }

      return savedItem;
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to add photo to queue:', error);
      throw new Error(`Failed to add photo to queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      if (activeStagingQueueId) {
        this.activeStagingQueueIds.delete(activeStagingQueueId);
      }
    }
  }

  /**
   * Get photos from queue with filtering options
   */
  async getQueuedPhotos(options: {
    status?: 'pending' | 'uploading' | 'uploaded' | 'failed';
    limit?: number;
    reviewResponseId?: number;
  } = {}): Promise<PhotoQueueItem[]> {
    try {
      this.captureEnvironmentKey();
      const photos = await offlineDatabase.getPhotosFromQueue({
        status: options.status,
        limit: options.limit,
      });

      // Filter by response ID if provided
      if (options.reviewResponseId) {
        return photos.filter(photo => photo.reviewResponseId === options.reviewResponseId);
      }

      return photos;
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to get queued photos:', error);
      throw new Error(`Failed to get queued photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get queued photos for a specific response (supports both server ID and temp ID)
   * Optionally filter by questionId to get photos for a specific question
   * Useful for displaying pending/uploading photos in the UI
   */
  async getQueuedPhotosForResponse(
    responseId?: number,
    responseTempId?: string,
    questionId?: string
  ): Promise<PhotoQueueItem[]> {
    try {
      if (!responseId && !responseTempId) {
        return [];
      }

      // Rendering may race startup initialization after iOS rotates the app's
      // data-container UUID during an install-over. Do not expose persisted
      // absolute paths until initialize() has repaired them for this container.
      await this.initialize();

      // Get all photos from queue (excluding fully uploaded ones after cleanup)
      const allPhotos = await this.getQueuedPhotos({});

      // Filter by matching responseId or tempId, then by questionId
      return allPhotos.filter(photo => {
        const matchesResponseId = responseId && photo.reviewResponseId === responseId;
        const matchesTempId = responseTempId && photo.tempId === responseTempId;

        // Check if photo matches the response
        if (!matchesResponseId && !matchesTempId) {
          return false;
        }

        // Filter by questionId to prevent cross-contamination between photo questions
        if (questionId !== undefined) {
          // When questionId is provided, only return photos tagged with that questionId
          return photo.metadata?.questionId === questionId;
        } else {
          // When questionId is undefined (item-level generic photos),
          // only return photos WITHOUT a questionId tag (don't mix in question-specific photos)
          return !photo.metadata?.questionId;
        }
      });
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to get queued photos for response:', error);
      return [];
    }
  }

  /**
   * Get pending photos count
   */
  async getPendingPhotosCount(): Promise<number> {
    try {
      const pending = await this.getQueuedPhotos({ status: 'pending' });
      return pending.length;
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to get pending photos count:', error);
      return 0;
    }
  }

  async updateQueuedPhotoNote(queueId: number, note: string): Promise<void> {
    await this.initialize();
    this.captureEnvironmentKey();
    const queuedPhoto = (await offlineDatabase.getPhotosFromQueue({}))
      .find(photo => photo.id === queueId);
    await offlineDatabase.updatePhotoQueueItem(queueId, {
      note,
      ...(queuedPhoto ? {
        metadata: {
          ...queuedPhoto.metadata,
          [LOCAL_PHOTO_NOTE_UPDATED_AT_KEY]: new Date().toISOString(),
        },
      } : {}),
    });

    const backendId = Number(queuedPhoto?.metadata?.backendId);
    if (Number.isFinite(backendId) && backendId > 0) {
      await offlineDatabase.updatePhotoMetadataNote(
        backendId,
        note,
        'pending'
      );
    }
  }

  async updateUploadedPhotoNote(
    photoId: number,
    note: string,
    fallback: UploadedPhotoNoteMetadata
  ): Promise<void> {
    await this.initialize();
    this.captureEnvironmentKey();

    const existing = await offlineDatabase.getPhotoMetadataById(photoId);
    if (existing) {
      await offlineDatabase.updatePhotoMetadataNote(photoId, note, 'pending');
    } else {
      if (!fallback.s3Key) {
        throw new Error('Uploaded photo metadata is not available for offline note storage');
      }
      await offlineDatabase.cachePhotoMetadata({
        id: photoId,
        responseId: fallback.responseId,
        s3Key: fallback.s3Key,
        s3Url: fallback.s3Url,
        fileName: fallback.fileName,
        mimeType: fallback.mimeType,
        size: fallback.size,
        caption: fallback.caption,
        note,
        noteSyncStatus: 'pending',
        noteSyncError: null,
        metadata: fallback.metadata ? JSON.stringify(fallback.metadata) : undefined,
        questionId: fallback.questionId,
        uploadedAt: fallback.uploadedAt,
      });
    }

    // A server-backed tile can replace its retained queued tile before the note
    // editor opens, so queueId is not always available here. Mirror the edit to
    // every current-environment alias for this exact backend photo and response.
    // photos_metadata remains the sync authority; this queue copy is only used
    // to reconcile the retained local tile while it awaits age-based cleanup.
    const localNoteUpdatedAt = new Date().toISOString();
    const queuedPhotos = await offlineDatabase.getPhotosFromQueue({});
    const queuedPhotosById = new Map(
      queuedPhotos.flatMap(photo => photo.id ? [[photo.id, photo] as const] : [])
    );
    const queueIdsToUpdate = new Set<number>();

    if (fallback.queueId) {
      queueIdsToUpdate.add(fallback.queueId);
    }

    for (const queuedPhoto of queuedPhotos) {
      const backendId = Number(queuedPhoto.metadata?.backendId);
      const responseId = Number(queuedPhoto.reviewResponseId);
      if (
        queuedPhoto.id &&
        backendId === Number(photoId) &&
        responseId === Number(fallback.responseId)
      ) {
        queueIdsToUpdate.add(queuedPhoto.id);
      }
    }

    for (const queueId of queueIdsToUpdate) {
      const queuedPhoto = queuedPhotosById.get(queueId);
      await offlineDatabase.updatePhotoQueueItem(queueId, {
        note,
        ...(queuedPhoto ? {
          metadata: {
            ...queuedPhoto.metadata,
            [LOCAL_PHOTO_NOTE_UPDATED_AT_KEY]: localNoteUpdatedAt,
          },
        } : {}),
      });
    }

    if (await this.isOnline()) {
      try {
        await this.syncPendingPhotoNotes();
      } catch (error) {
        // The durable metadata row remains failed/pending for the next full sync.
        console.warn('[PhotoQueueService] Photo note saved locally and will retry:', error);
      }
    }
  }

  async syncPendingPhotoNotes(): Promise<void> {
    await this.initialize();
    if (!await this.isOnline()) {
      return;
    }

    const pendingNotes = await offlineDatabase.getPendingPhotoNotes();
    let firstError: unknown;

    for (const pendingNote of pendingNotes) {
      try {
        await offlineDatabase.updatePhotoMetadataNote(
          pendingNote.id,
          pendingNote.note,
          'syncing'
        );
        const updatedPhoto = await reviewPhotoApi.updatePhotoNote(
          pendingNote.id,
          pendingNote.note
        );
        await offlineDatabase.updatePhotoMetadataNote(
          pendingNote.id,
          updatedPhoto.note ?? pendingNote.note,
          'synced'
        );
      } catch (error) {
        firstError ||= error;
        await offlineDatabase.updatePhotoMetadataNote(
          pendingNote.id,
          pendingNote.note,
          'failed',
          error instanceof Error ? error.message : 'Photo note sync failed'
        );
      }
    }

    if (firstError) {
      throw firstError;
    }
  }

  /**
   * Process upload queue - upload pending photos
   */
  async processUploadQueue(
    options: ForcedPhotoRetryOptions = {}
  ): Promise<BatchUploadResult> {
    await this.initialize();
    this.captureEnvironmentKey();

    if (this.uploadInProgress) {
      this.uploadRerunRequested = true;
      return { successful: [], failed: [], totalProcessed: 0 };
    }

    if (!await this.isOnline()) {
      return { successful: [], failed: [], totalProcessed: 0 };
    }

    this.uploadInProgress = true;
    const result: BatchUploadResult = { successful: [], failed: [], totalProcessed: 0 };

    try {
      const queuedPhotos = await this.getQueuedPhotos({});
      const uploadCandidates = queuedPhotos.filter(photo =>
        this.matchesRetryScope(photo, options) && (
          photo.status === 'pending' ||
          (photo.status === 'failed' && this.shouldRetryFailedPhoto(photo))
        )
      );
      const sortedPhotos = this.sortPhotosByPriority(uploadCandidates);

      // Process in batches
      for (let i = 0; i < sortedPhotos.length; i += this.BATCH_SIZE) {
        if (!await this.isOnline()) {
          break;
        }

        const batch = sortedPhotos.slice(i, i + this.BATCH_SIZE);
        const batchResults = await this.processBatch(batch);

        result.successful.push(...batchResults.successful);
        result.failed.push(...batchResults.failed);
        result.totalProcessed += batchResults.totalProcessed;
      }
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to process upload queue:', error);
    } finally {
      this.uploadInProgress = false;
      if (this.uploadRerunRequested) {
        this.uploadRerunRequested = false;
        setTimeout(() => {
          this.processUploadQueue().catch(error => {
            console.warn('[PhotoQueueService] Deferred upload queue rerun failed:', error);
          });
        }, 0);
      }
    }

    return result;
  }

  /**
   * Retry failed uploads
   */
  async retryFailedUploads(): Promise<BatchUploadResult> {
    try {
      await this.initialize();
      const failedPhotos = await this.getQueuedPhotos({ status: 'failed' });
      const retryablePhotos = failedPhotos.filter(photo => this.shouldRetryFailedPhoto(photo));

      if (retryablePhotos.length === 0) {
        return { successful: [], failed: [], totalProcessed: 0 };
      }

      // Reset status to pending for retry
      for (const photo of retryablePhotos) {
        await offlineDatabase.updatePhotoQueueItem(photo.id!, {
          status: 'pending',
          errorMessage: undefined,
        });
      }

      // Process the retry queue
      return await this.processUploadQueue();
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to retry failed uploads:', error);
      return { successful: [], failed: [], totalProcessed: 0 };
    }
  }

  /**
   * Explicitly reset failed uploads, including items that exhausted automatic retries.
   * Missing files remain failed and are never discarded.
   */
  async forceRetryFailedUploads(
    options: ForcedPhotoRetryOptions = {}
  ): Promise<ForcedPhotoRetryResult> {
    await this.initialize();
    const allPhotos = await this.getQueuedPhotos({});
    const retryPhotos = allPhotos.filter(photo =>
      photo.status !== 'uploaded' &&
      !Boolean(photo.id && this.activeStagingQueueIds.has(photo.id)) &&
      this.matchesRetryScope(photo, options)
    );
    let reset = 0;

    for (const photo of retryPhotos) {
      if (!photo.id) {
        continue;
      }

      const resolved = await this.ensureQueuedPhotoFile(photo, true);
      if (!resolved.exists) {
        if (!resolved.uncertain) {
          await offlineDatabase.updatePhotoQueueItem(photo.id, {
            status: 'failed',
            errorMessage: PHOTO_FILE_MISSING_ERROR,
          });
        }
        continue;
      }

      if (this.isStorageStagingPhoto(photo)) {
        if (await this.promoteRecoveredStagingPhoto(
          photo,
          resolved,
          { allowLegacyRecovery: true }
        )) {
          reset += 1;
        }
        continue;
      }

      if (photo.status === 'failed' || photo.status === 'uploading') {
        await offlineDatabase.updatePhotoQueueItem(photo.id, {
          localPath: resolved.path,
          status: 'pending',
          retryCount: 0,
          errorMessage: null,
        });
        reset += 1;
      }
    }

    const batchResult = await this.processUploadQueue(options);
    const remainingPhotos = await this.getQueuedPhotos({});
    const missing = remainingPhotos.filter(photo =>
      this.matchesRetryScope(photo, options) &&
      photo.status !== 'uploaded' &&
      photo.errorMessage === PHOTO_FILE_MISSING_ERROR
    ).length;
    const stillFailed = remainingPhotos.filter(photo =>
      this.matchesRetryScope(photo, options) &&
      photo.status !== 'uploaded' &&
      photo.errorMessage !== PHOTO_FILE_MISSING_ERROR
    ).length;

    return {
      ...batchResult,
      reset,
      missing,
      stillFailed,
    };
  }

  getLastRepairResult(): PhotoQueueRepairResult {
    return {
      ...this.lastRepairResult,
      missingPhotoIds: [...this.lastRepairResult.missingPhotoIds],
    };
  }

  async getLocalFileStats(): Promise<LocalPhotoFileStats> {
    const photos = await this.getQueuedPhotos({});
    let available = 0;
    let missing = 0;

    for (const photo of photos.filter(candidate => candidate.status !== 'uploaded')) {
      const canonicalPath = this.getCanonicalStoragePath(photo);
      const hasCanonical =
        Boolean(canonicalPath) &&
        await this.isUsableQueueFile(canonicalPath!, photo.size);
      const normalizedStoredPath = localPathFromUri(photo.localPath);
      const hasStoredFile =
        this.isReadableMigrationSource(normalizedStoredPath, photo.fileName) &&
        await this.isUsableQueueFile(normalizedStoredPath, photo.size);
      const recoverableStagingPath = this.getRecoverableStagingPath(photo);
      const hasRecoverableStagingFile =
        Boolean(recoverableStagingPath) &&
        await this.isUsableQueueFile(recoverableStagingPath!, photo.size);
      if (hasCanonical || hasStoredFile || hasRecoverableStagingFile) {
        available += 1;
      } else {
        missing += 1;
      }
    }

    return { available, missing };
  }

  /**
   * Remove photo from queue and clean up local file
   */
  async removeFromQueue(photoId: number): Promise<void> {
    try {
      // Get photo info before deletion
      const photos = await this.getQueuedPhotos({});
      const photo = photos.find(p => p.id === photoId);

      if (photo) {
        await this.deleteQueueItemFile(photo);
      }

      // Remove from database
      await offlineDatabase.removePhotoFromQueue(photoId);
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to remove photo from queue:', error);
      throw new Error(`Failed to remove photo from queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clear all photos from queue (use with caution)
   */
  async clearQueue(): Promise<void> {
    try {
      const allPhotos = await this.getQueuedPhotos({});

      // Delete all local files
      for (const photo of allPhotos) {
        await this.deleteQueueItemFile(photo);
      }

      // Clear database records
      for (const photo of allPhotos) {
        if (photo.id) {
          await offlineDatabase.removePhotoFromQueue(photo.id);
        }
      }
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to clear photo queue:', error);
      throw new Error(`Failed to clear photo queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<{
    pending: number;
    uploading: number;
    uploaded: number;
    failed: number;
    totalSize: number;
  }> {
    try {
      const allPhotos = await this.getQueuedPhotos({});

      const stats = {
        pending: 0,
        uploading: 0,
        uploaded: 0,
        failed: 0,
        totalSize: 0,
      };

      for (const photo of allPhotos) {
        stats[photo.status]++;
        stats.totalSize += photo.size || 0;
      }

      return stats;
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to get queue stats:', error);
      return { pending: 0, uploading: 0, uploaded: 0, failed: 0, totalSize: 0 };
    }
  }

  /**
   * Subscribe to upload completion events
   * @param callback Function to call when a photo upload completes
   * @returns Unsubscribe function
   */
  onUploadComplete(callback: (queueId: number, backendId?: number) => void): () => void {
    this.uploadCompletionCallbacks.push(callback);
    return () => {
      const index = this.uploadCompletionCallbacks.indexOf(callback);
      if (index > -1) {
        this.uploadCompletionCallbacks.splice(index, 1);
      }
    };
  }

  // Private helper methods

  /**
   * Ensure local storage directory exists
   */
  private async ensureStorageDirectory(): Promise<void> {
    try {
      for (const directory of [
        this.LEGACY_STORAGE_DIR,
        this.LOCAL_STORAGE_DIR,
      ]) {
        const exists = await RNFS.exists(directory);
        if (!exists) {
          await RNFS.mkdir(directory);
        }
      }
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to create storage directory:', error);
      throw error;
    }
  }

  private assertLifecycleGeneration(expectedGeneration: number): void {
    if (this.lifecycleGeneration !== expectedGeneration) {
      throw new Error('Photo queue initialization was cancelled');
    }
  }

  /**
   * Check network connectivity
   */
  private async isOnline(): Promise<boolean> {
    try {
      return await getEffectiveNetworkOnline();
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to check network status:', error);
      return false;
    }
  }

  /**
   * Start monitoring network changes
   */
  private startNetworkMonitoring(environmentKey: string): void {
    const handleOnlineState = (isOnline: boolean) => {
      if (isOnline && !shouldUseOfflineReviewWorkflow() && !this.uploadInProgress) {
        // Delay to ensure connection is stable
        setTimeout(() => {
          if (
            !this.initialized ||
            this.activeEnvironmentKey !== environmentKey ||
            getOfflineEnvironmentKey() !== environmentKey
          ) {
            return;
          }
          this.processUploadQueue().catch(error => {
            console.warn(
              '[PhotoQueueService] Network-triggered upload queue processing failed:',
              error
            );
          });
        }, 2000);
      }
    };

    const unsubscribeNetInfo = NetInfo.addEventListener(state => {
      handleOnlineState(applyDetoxNetworkOverride(state));
    });
    const unsubscribeDetoxOverride = subscribeToDetoxNetworkOverride(handleOnlineState);

    this.networkListener = () => {
      unsubscribeNetInfo();
      unsubscribeDetoxOverride();
    };
  }

  /**
   * Sort photos by priority and creation date
   */
  private sortPhotosByPriority(photos: PhotoQueueItem[]): PhotoQueueItem[] {
    return photos.sort((a, b) => {
      // Priority order: high > normal > low
      const priorityOrder = { high: 3, normal: 2, low: 1 };
      const aPriority = priorityOrder[(a.metadata?.priority as keyof typeof priorityOrder) || 'normal'];
      const bPriority = priorityOrder[(b.metadata?.priority as keyof typeof priorityOrder) || 'normal'];

      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }

      // Same priority, sort by creation date (oldest first)
      return a.createdAt.getTime() - b.createdAt.getTime();
    });
  }

  /**
   * Process a batch of photos
   */
  private async processBatch(photos: PhotoQueueItem[]): Promise<BatchUploadResult> {
    const result: BatchUploadResult = { successful: [], failed: [], totalProcessed: 0 };

    for (const photo of photos) {
      if (!photo.id) continue;

      result.totalProcessed++;

      try {
        // Mark as uploading
        await offlineDatabase.updatePhotoQueueItem(photo.id, {
          status: 'uploading',
          lastAttemptAt: new Date(),
        });

        // Upload the photo
        const backendId = await this.uploadSinglePhoto(photo);

        // Mark as uploaded and cleanup
        await offlineDatabase.updatePhotoQueueItem(photo.id, {
          status: 'uploaded',
          lastAttemptAt: new Date(),
          errorMessage: undefined,
        });

        // Subscribers must only observe completion after the durable queue row
        // has reached its terminal uploaded state.
        this.uploadCompletionCallbacks.forEach(callback => {
          try {
            callback(photo.id!, backendId);
          } catch (callbackError) {
            console.warn('[PhotoQueueService] Upload completion callback failed:', callbackError);
          }
        });

        result.successful.push(photo);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        const isCurrentlyOnline = await this.isOnline().catch(() => true);
        const isAwaitingSync = !isCurrentlyOnline || this.isSyncDependencyError(errorMessage);

        if (isAwaitingSync) {
          // Waiting for response to sync - don't increment retry count
          console.error('[PhotoQueueService] ⏳ Photo waiting for response sync:', photo.tempId || photo.reviewResponseId);
          await offlineDatabase.updatePhotoQueueItem(photo.id, {
            status: 'pending',
            errorMessage: errorMessage,
            lastAttemptAt: new Date(),
            // Don't increment retryCount - this isn't a real failure
          });

          result.failed.push(photo);
        } else {
          // Actual upload failure - increment retry count
          console.error('[PhotoQueueService] ❌ Photo upload failed:', photo.id, errorMessage);

          const newRetryCount = photo.retryCount + 1;
          const status = newRetryCount >= this.MAX_RETRIES ? 'failed' : 'pending';

          await offlineDatabase.updatePhotoQueueItem(photo.id, {
            status,
            retryCount: newRetryCount,
            errorMessage: errorMessage,
            lastAttemptAt: new Date(),
          });

          result.failed.push({ ...photo, retryCount: newRetryCount, status });

          // Add retry delay for actual failures
          if (newRetryCount < this.MAX_RETRIES) {
            const delay = this.RETRY_DELAYS[Math.min(newRetryCount - 1, this.RETRY_DELAYS.length - 1)];
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    }

    return result;
  }

  private shouldRetryFailedPhoto(photo: PhotoQueueItem): boolean {
    return !this.isStorageStagingPhoto(photo) &&
      photo.errorMessage !== PHOTO_FILE_MISSING_ERROR && (
      photo.retryCount < this.MAX_RETRIES ||
      this.isSyncDependencyError(photo.errorMessage)
    );
  }

  private isStorageStagingPhoto(
    photo: Pick<PhotoQueueItem, 'metadata'>
  ): boolean {
    return [
      PHOTO_STORAGE_STATE_STAGING,
      PHOTO_STORAGE_STATE_COPYING,
      PHOTO_STORAGE_STATE_READY,
    ].includes(photo.metadata?.storageState);
  }

  private isUnverifiedStorageCopyingPhoto(photo: PhotoQueueItem): boolean {
    return photo.metadata?.storageState === PHOTO_STORAGE_STATE_COPYING;
  }

  private matchesRetryScope(
    photo: PhotoQueueItem,
    options: ForcedPhotoRetryOptions
  ): boolean {
    const photoIds = options.photoIds || [];
    const responseIds = options.reviewResponseIds || [];
    const tempIds = options.responseTempIds || [];
    const hasScope = photoIds.length > 0 || responseIds.length > 0 || tempIds.length > 0;

    return !hasScope ||
      Boolean(photo.id && photoIds.includes(photo.id)) ||
      responseIds.includes(photo.reviewResponseId) ||
      Boolean(photo.tempId && tempIds.includes(photo.tempId));
  }

  private isSyncDependencyError(errorMessage?: string | null): boolean {
    return Boolean(
      errorMessage?.includes('Response is not ready to sync yet') ||
      errorMessage?.includes('Review is not ready to sync') ||
      errorMessage?.includes('Review is not ready to sync photos yet')
    );
  }

  /**
   * Upload a single photo using the review service
   */
  private async uploadSinglePhoto(photo: PhotoQueueItem): Promise<number> {
    if (!photo.metadata) {
      throw new Error('Photo metadata is required for upload');
    }

    const { itemId, questionId } = photo.metadata;

    if (!this.isSafeQueueFileName(photo.fileName)) {
      throw new Error('Queued photo filename is not a safe basename');
    }

    // Every upload must prove the v1-layout rollback anchor first. A readable
    // v2 localPath alone is insufficient during the expand release.
    const resolvedLocalFile = await this.ensureQueuedPhotoFile(photo, true);
    if (!resolvedLocalFile.exists) {
      throw new Error(
        resolvedLocalFile.uncertain
          ? PHOTO_FILE_UNVERIFIED_ERROR
          : PHOTO_FILE_MISSING_ERROR
      );
    }

    const realResponseId = await this.resolveUploadResponseId(photo);
    const realReviewId = await this.resolveUploadReviewId(photo);

    // Prepare photo for upload
    const uploadUri =
      resolvedLocalFile.path.startsWith('file://') ||
      resolvedLocalFile.path.startsWith('content://')
      ? resolvedLocalFile.path
      : `file://${resolvedLocalFile.path}`;

    const photoData = {
      uri: uploadUri,
      name: photo.fileName,
      type: photo.mimeType,
    };

    try {
      // Upload using focused photo API with resolved IDs and capture response
      const uploadedPhotosData = await reviewPhotoApi.uploadPhotos(
        realReviewId,
        realResponseId,
        itemId,
        [photoData],
        questionId
      );

      const uploadedPhoto = uploadedPhotosData?.[0];
      if (!uploadedPhoto?.id) {
        throw new Error('Photo upload was not confirmed by the server');
      }

      const latestQueuePhoto = photo.id
        ? (await offlineDatabase.getPhotosFromQueue({}))
          .find(candidate => candidate.id === photo.id)
        : undefined;
      const queuedNote = latestQueuePhoto?.note ?? photo.note;
      let uploadedNote = uploadedPhoto.note ?? queuedNote ?? null;
      let noteSyncStatus: 'pending' | 'synced' = 'synced';
      let noteSyncError: string | null = null;
      if (queuedNote !== undefined && queuedNote !== null) {
        try {
          const noteResult = await reviewPhotoApi.updatePhotoNote(
            Number(uploadedPhoto.id),
            queuedNote
          );
          uploadedNote = noteResult.note ?? queuedNote;
        } catch (noteError) {
          noteSyncStatus = 'pending';
          noteSyncError = noteError instanceof Error
            ? noteError.message
            : 'Photo note sync failed';
          console.warn('[PhotoQueueService] Photo uploaded; note will retry during sync:', noteError);
        }
      }

      // Persist the server-confirmed photo metadata before the queue item is marked uploaded.
      if (photo.id) {
        await offlineDatabase.cachePhotoMetadata({
          id: Number(uploadedPhoto.id),
          responseId: Number(uploadedPhoto.reviewResponseId || realResponseId),
          s3Key: uploadedPhoto.s3Key,
          s3Url: uploadedPhoto.s3Url,
          fileName: uploadedPhoto.fileName || photo.fileName,
          mimeType: uploadedPhoto.mimeType || photo.mimeType,
          size: uploadedPhoto.size || photo.size,
          caption: uploadedPhoto.caption,
          note: uploadedNote,
          noteSyncStatus,
          noteSyncError,
          metadata: uploadedPhoto.metadata ? JSON.stringify(uploadedPhoto.metadata) : undefined,
          questionId: uploadedPhoto.questionId || questionId,
          uploadedAt: uploadedPhoto.uploadedAt || new Date(),
        });

        // Update queue item with backendId in metadata
        await offlineDatabase.updatePhotoQueueItem(photo.id, {
          metadata: {
            ...photo.metadata,
            backendId: uploadedPhoto.id,
          },
        });
        if (photo.metadata?.offlineMutationId) {
          await offlineDatabase.updateOfflineMutationStatus?.(
            Number(photo.metadata.offlineMutationId),
            'synced',
            {
              backendPhotoId: uploadedPhoto.id,
              responseId: uploadedPhoto.reviewResponseId || realResponseId,
            }
          );
        }
      }

      return Number(uploadedPhoto.id);
    } catch (error) {
      console.warn('[PhotoQueueService] Upload API call failed:', error);
      throw error;
    }
  }

  private async resolveUploadResponseId(photo: PhotoQueueItem): Promise<number> {
    let realResponseId = photo.reviewResponseId;
    const responseIdForItem = await this.resolveResponseIdByItem(photo);

    if (responseIdForItem) {
      realResponseId = responseIdForItem;
    } else if (photo.tempId) {
      const mappedId = await offlineDatabase.getRealIdFromTempId(photo.tempId);
      if (mappedId) {
        realResponseId = mappedId;
      } else {
        throw new Error('Response is not ready to sync yet');
      }
    } else if (realResponseId < 0) {
      realResponseId = responseIdForItem ?? realResponseId;
    }

    if (realResponseId < 0) {
      const identifier = photo.tempId
        ? `temp ID: ${photo.tempId}`
        : `negative ID: ${photo.reviewResponseId}`;
      console.warn('[PhotoQueueService] Response ID is not ready for photo sync:', identifier);
      throw new Error('Response is not ready to sync yet');
    }

    if (photo.id && (photo.reviewResponseId !== realResponseId || photo.tempId)) {
      await offlineDatabase.updatePhotoQueueItem(photo.id, {
        reviewResponseId: realResponseId,
        tempId: null as any,
      });
    }

    if (photo.tempId) {
      await offlineDatabase.saveIdMapping?.(photo.tempId, realResponseId, 'response');
    }

    return realResponseId;
  }

  private async resolveUploadReviewId(photo: PhotoQueueItem): Promise<number> {
    const metadata = photo.metadata || {};
    const queuedReviewId = Number(metadata.reviewId);

    if (!Number.isFinite(queuedReviewId)) {
      throw new Error('Review is not ready to sync photos yet');
    }

    const localReview = await offlineDatabase.getReview?.(queuedReviewId).catch(error => {
      console.warn('[PhotoQueueService] Failed to load queued photo review for ID resolution:', {
        reviewId: queuedReviewId,
        queueId: photo.id,
        error,
      });
      return null;
    });
    const reviewTempId = localReview?.tempId;

    if (reviewTempId) {
      const mappedReviewId = await offlineDatabase.getRealIdFromTempId(reviewTempId);
      if (mappedReviewId) {
        if (photo.id) {
          await offlineDatabase.updatePhotoQueueItem(photo.id, {
            metadata: {
              ...metadata,
              localReviewId: queuedReviewId,
              reviewId: mappedReviewId,
            },
          });
        }
        return mappedReviewId;
      }

      throw new Error('Review is not ready to sync photos yet');
    }

    if (Number.isFinite(queuedReviewId) && queuedReviewId > 0) {
      return queuedReviewId;
    }

    throw new Error('Review is not ready to sync photos yet');
  }

  private async resolveResponseIdByItem(photo: PhotoQueueItem): Promise<number | null> {
    const reviewId = Number(photo.metadata?.reviewId);
    const itemId = Number(photo.metadata?.itemId);
    if (!reviewId || !itemId) {
      return null;
    }

    try {
      const responses = await offlineDatabase.getResponsesByReviewId(reviewId);
      const winningResponse = selectBestResponseForItem(responses as any[], itemId);
      if (winningResponse?.tempId) {
        const mappedId = await offlineDatabase.getRealIdFromTempId(winningResponse.tempId);
        if (mappedId) {
          console.warn('[PhotoQueueService] Recovered queued photo response temp mapping by itemId:', {
            reviewId,
            itemId,
            responseId: mappedId,
            queueId: photo.id,
            tempId: winningResponse.tempId,
          });
          return mappedId;
        }

        return null;
      }

      const winningId = Number(winningResponse?.id);
      if (Number.isFinite(winningId) && winningId > 0) {
        console.warn('[PhotoQueueService] Recovered queued photo response mapping by itemId:', {
          reviewId,
          itemId,
          responseId: winningId,
          queueId: photo.id,
          tempId: photo.tempId,
        });
        return winningId;
      }
    } catch (error) {
      console.warn('[PhotoQueueService] Failed to resolve queued photo response by itemId:', {
        reviewId,
        itemId,
        queueId: photo.id,
        error,
      });
    }

    return null;
  }

  private isSafeQueueFileName(fileName: string): boolean {
    return (
      typeof fileName === 'string' &&
      fileName.length > 0 &&
      fileName.length <= 240 &&
      fileName !== '.' &&
      fileName !== '..' &&
      !/[\/\\\u0000-\u001f\u007f]/.test(fileName)
    );
  }

  private getDirectChildFileName(rootPath: string, filePath: string): string | null {
    const prefix = `${rootPath.replace(/\/+$/, '')}/`;
    if (!filePath.startsWith(prefix)) {
      return null;
    }

    const fileName = filePath.slice(prefix.length);
    return this.isSafeQueueFileName(fileName) ? fileName : null;
  }

  private getCanonicalStoragePath(
    photo: Pick<PhotoQueueItem, 'fileName'>
  ): string | null {
    return this.isSafeQueueFileName(photo.fileName)
      ? `${this.LOCAL_STORAGE_DIR}/${photo.fileName}`
      : null;
  }

  private getStagingFilePath(
    fileName: string,
    state: 'copying' | 'ready'
  ): string | null {
    if (!this.isSafeQueueFileName(fileName)) {
      return null;
    }

    const stagingFileName = `.${state}-${fileName}`;
    return this.isSafeQueueFileName(stagingFileName)
      ? `${this.LOCAL_STORAGE_DIR}/${stagingFileName}`
      : null;
  }

  private getRecoverableStagingPath(photo: PhotoQueueItem): string | null {
    if (
      !this.isStorageStagingPhoto(photo) ||
      !this.isSafeQueueFileName(photo.fileName)
    ) {
      return null;
    }

    return this.getStagingFilePath(photo.fileName, 'ready');
  }

  private getLegacyStoragePath(
    photo: Pick<PhotoQueueItem, 'fileName'>
  ): string | null {
    return this.isSafeQueueFileName(photo.fileName)
      ? `${this.LEGACY_STORAGE_DIR}/${photo.fileName}`
      : null;
  }

  private getLegacyPendingPath(
    photo: Pick<PhotoQueueItem, 'fileName'>
  ): string | null {
    if (!this.isSafeQueueFileName(photo.fileName)) {
      return null;
    }
    const pendingFileName = `.pending-${photo.fileName}`;
    return this.isSafeQueueFileName(pendingFileName)
      ? `${this.LEGACY_STORAGE_DIR}/${pendingFileName}`
      : null;
  }

  private isPathInActiveStorage(filePath: string, expectedFileName: string): boolean {
    return this.getDirectChildFileName(this.LOCAL_STORAGE_DIR, filePath) ===
      expectedFileName;
  }

  private isReadableMigrationSource(
    filePath: string,
    expectedFileName: string,
    allowExternalOriginal = false
  ): boolean {
    const v2Prefix = `${this.V2_STORAGE_ROOT.replace(/\/+$/, '')}/`;
    if (filePath.startsWith(v2Prefix)) {
      return this.isPathInActiveStorage(filePath, expectedFileName);
    }

    const legacyPrefix = `${this.LEGACY_STORAGE_DIR.replace(/\/+$/, '')}/`;
    if (filePath.startsWith(legacyPrefix)) {
      return (
        this.getDirectChildFileName(this.LEGACY_STORAGE_DIR, filePath) ===
        expectedFileName
      );
    }

    return allowExternalOriginal || (
      filePath.startsWith('/') &&
      filePath.endsWith(`/photos_queue/${expectedFileName}`)
    );
  }

  /** Delete only a matching direct queue child explicitly authorized by its caller. */
  private async deleteConfinedQueueFile(
    filePath: string,
    expectedFileName: string,
    options: { allowLegacy?: boolean } = {}
  ): Promise<boolean> {
    if (!this.isSafeQueueFileName(expectedFileName)) {
      console.warn('[PhotoQueueService] Refusing queue deletion with unsafe filename:', {
        filePath,
        expectedFileName,
      });
      return false;
    }

    const isActiveFile =
      this.getDirectChildFileName(this.LOCAL_STORAGE_DIR, filePath) === expectedFileName;
    const isLegacyFile =
      Boolean(options.allowLegacy) &&
      this.getDirectChildFileName(this.LEGACY_STORAGE_DIR, filePath) === expectedFileName;

    if (!isActiveFile && !isLegacyFile) {
      console.warn('[PhotoQueueService] Refusing queue deletion outside owned storage:', {
        filePath,
        expectedFileName,
      });
      return false;
    }

    if (!(await RNFS.exists(filePath))) {
      return true;
    }

    const stats = await RNFS.stat(filePath);
    if (!isRegularFile(stats)) {
      console.warn('[PhotoQueueService] Refusing to recursively delete queue entry:', {
        filePath,
        expectedFileName,
      });
      return false;
    }

    await RNFS.unlink(filePath);
    return true;
  }

  private async deleteQueueItemFile(
    photo: Pick<PhotoQueueItem, 'localPath' | 'fileName' | 'metadata'>
  ): Promise<void> {
    const normalizedLocalPath = localPathFromUri(photo.localPath);
    const activeLocalFileName =
      this.getDirectChildFileName(this.LOCAL_STORAGE_DIR, normalizedLocalPath);
    const allowedActiveFileNames = new Set(
      [
        photo.fileName,
        `.copying-${photo.fileName}`,
        `.ready-${photo.fileName}`,
      ].filter(fileName => this.isSafeQueueFileName(fileName))
    );
    const activeDeletionTargets = new Map<string, string>();
    if (
      activeLocalFileName &&
      allowedActiveFileNames.has(activeLocalFileName)
    ) {
      activeDeletionTargets.set(normalizedLocalPath, activeLocalFileName);
    }
    if (
      photo.metadata?.rollbackFileAllocated === true &&
      this.isStorageStagingPhoto(photo)
    ) {
      for (const candidate of [
        this.getStagingFilePath(photo.fileName, 'copying'),
        this.getStagingFilePath(photo.fileName, 'ready'),
        this.getCanonicalStoragePath(photo),
      ]) {
        if (!candidate) {
          continue;
        }
        const candidateFileName =
          this.getDirectChildFileName(this.LOCAL_STORAGE_DIR, candidate);
        if (candidateFileName) {
          activeDeletionTargets.set(candidate, candidateFileName);
        }
      }
    }

    if (activeDeletionTargets.size > 0) {
      for (const [filePath, fileName] of activeDeletionTargets) {
        try {
          await this.deleteConfinedQueueFile(filePath, fileName);
        } catch (error) {
          console.warn(
            '[PhotoQueueService] Failed to delete confined queue file:',
            filePath,
            error
          );
        }
      }
      return;
    }

    // The v1 directory is global across API environments. During expand, a
    // checksum match cannot prove that this row exclusively owns those bytes,
    // so explicit discard removes the row but leaves the rollback file for the
    // later contract cleanup.
    const legacyPath = this.getLegacyStoragePath(photo);
    if (
      legacyPath &&
      this.getDirectChildFileName(this.LEGACY_STORAGE_DIR, normalizedLocalPath) ===
        photo.fileName
    ) {
      console.warn(
        '[PhotoQueueService] Preserving shared rollback copy during explicit deletion:',
        { fileName: photo.fileName }
      );
    }
  }

  private async isUsableQueueFile(
    filePath: string,
    expectedSize?: number
  ): Promise<boolean> {
    return await this.getQueueFileState(filePath, expectedSize) === 'valid';
  }

  private async getQueueFileState(
    filePath: string,
    expectedSize?: number
  ): Promise<'missing' | 'valid' | 'invalid' | 'unknown'> {
    try {
      if (!(await RNFS.exists(filePath))) {
        return 'missing';
      }

      const stats = await RNFS.stat(filePath);
      if (!isRegularFile(stats)) {
        return 'invalid';
      }

      const actualSize = Number(stats.size);
      if (!Number.isFinite(actualSize) || actualSize <= 0) {
        return 'invalid';
      }

      return (!Number.isFinite(expectedSize) ||
        Number(expectedSize) <= 0 ||
        actualSize === Number(expectedSize)) ? 'valid' : 'invalid';
    } catch {
      return 'unknown';
    }
  }

  private async getQueueFileDigest(filePath: string): Promise<string> {
    const digest = await RNFS.hash(filePath, 'sha256');
    if (
      typeof digest !== 'string' ||
      !/^[a-f0-9]{64}$/i.test(digest)
    ) {
      throw new Error('Queued photo checksum is not verifiable');
    }
    return digest.toLowerCase();
  }

  private getStoredQueueChecksum(photo: PhotoQueueItem): string | null {
    const checksum = photo.metadata?.storageChecksum;
    return (
      typeof checksum === 'string' &&
      /^[a-f0-9]{64}$/i.test(checksum)
    ) ? checksum.toLowerCase() : null;
  }

  /**
   * Preserve a conflicting active-environment copy before retrying from a
   * hash-verified original. Identical repeats collapse to one retained copy.
   */
  private async quarantineCopyingStage(
    photo: PhotoQueueItem,
    copyingPath: string
  ): Promise<void> {
    const copyingDigest = await this.getQueueFileDigest(copyingPath);
    const quarantineFileName =
      `.quarantined-${photo.id || 'untracked'}-${copyingDigest}`;
    if (!this.isSafeQueueFileName(quarantineFileName)) {
      throw new Error('Queued photo quarantine filename is not safe');
    }
    const quarantinePath =
      `${this.LOCAL_STORAGE_DIR}/${quarantineFileName}`;
    const quarantineState = await this.getQueueFileState(quarantinePath);
    if (quarantineState === 'missing') {
      await RNFS.moveFile(copyingPath, quarantinePath);
      return;
    }
    if (quarantineState !== 'valid') {
      throw new RollbackPathConflictError(
        'Existing queued photo quarantine is not safely reusable'
      );
    }
    if (
      await this.getQueueFileDigest(quarantinePath) !== copyingDigest
    ) {
      throw new RollbackPathConflictError(
        'Existing queued photo quarantine checksum conflicts'
      );
    }

    const copyingFileName =
      this.getDirectChildFileName(this.LOCAL_STORAGE_DIR, copyingPath);
    if (
      !copyingFileName ||
      !await this.deleteConfinedQueueFile(copyingPath, copyingFileName)
    ) {
      throw new Error('Unable to retain and clear duplicate copying bytes');
    }
  }

  private async quarantineAllocatedLegacyConflict(
    photo: PhotoQueueItem,
    legacyPath: string
  ): Promise<void> {
    const legacyDigest = await this.getQueueFileDigest(legacyPath);
    const quarantineFileName =
      `.quarantined-${photo.id || 'untracked'}-${legacyDigest}`;
    if (!this.isSafeQueueFileName(quarantineFileName)) {
      throw new Error('Rollback quarantine filename is not safe');
    }
    const quarantinePath =
      `${this.LEGACY_STORAGE_DIR}/${quarantineFileName}`;
    const quarantineState = await this.getQueueFileState(quarantinePath);
    if (quarantineState === 'missing') {
      await RNFS.moveFile(legacyPath, quarantinePath);
      return;
    }
    if (quarantineState !== 'valid') {
      throw new RollbackPathConflictError(
        'Existing rollback quarantine is not safely reusable'
      );
    }
    if (
      await this.getQueueFileDigest(quarantinePath) !== legacyDigest
    ) {
      throw new RollbackPathConflictError(
        'Existing rollback quarantine checksum conflicts'
      );
    }
    if (
      !await this.deleteConfinedQueueFile(
        legacyPath,
        photo.fileName,
        { allowLegacy: true }
      )
    ) {
      throw new Error('Unable to retain and clear duplicate rollback bytes');
    }
  }

  /**
   * Publish an ephemeral picker/provider source through deterministic active-v2
   * staging. A complete `.ready-*` file is always recoverable by filename after
   * a kill; random repair temps are reserved for copies with another durable
   * source.
   */
  private async publishEphemeralSourceToReady(
    photo: PhotoQueueItem,
    sourcePath: string,
    options: {
      originalUri?: string;
      isContentUri?: boolean;
      acceptExistingCopying?: boolean;
    } = {}
  ): Promise<{
    path: string;
    size: number;
    digest: string;
    metadata: PhotoQueueItem['metadata'];
  }> {
    const copyingPath = this.getStagingFilePath(photo.fileName, 'copying');
    const readyPath = this.getStagingFilePath(photo.fileName, 'ready');
    if (!copyingPath || !readyPath) {
      throw new Error('Queued photo staging path is not safe');
    }

    let checkpointMetadata = {
      ...photo.metadata,
      storageState: PHOTO_STORAGE_STATE_COPYING,
    };
    if (
      photo.id &&
      photo.metadata?.storageState !== PHOTO_STORAGE_STATE_COPYING
    ) {
      await offlineDatabase.updatePhotoQueueItem(photo.id, {
        metadata: checkpointMetadata,
      });
    }
    photo.metadata = checkpointMetadata;

    const copyAndVerifySource = async (
      expectedDigest?: string | null
    ): Promise<string> => {
      await this.copyPhotoToQueueStorage(sourcePath, copyingPath, options);
      if (!(await this.isUsableQueueFile(copyingPath, photo.size))) {
        throw new Error('Copied photo failed staging verification');
      }
      const copyingDigest = await this.getQueueFileDigest(copyingPath);
      const storedChecksum = this.getStoredQueueChecksum(photo);
      if (storedChecksum && storedChecksum !== copyingDigest) {
        throw new RollbackPathConflictError(
          'Copied photo checksum changed during staging'
        );
      }
      if (expectedDigest && expectedDigest !== copyingDigest) {
        throw new RollbackPathConflictError(
          'Copied photo checksum changed during staging'
        );
      }
      try {
        const sourceDigest =
          await this.getQueueFileDigest(localPathFromUri(sourcePath));
        if (sourceDigest !== copyingDigest) {
          throw new RollbackPathConflictError(
            'Copied photo checksum changed during staging'
          );
        }
      } catch (error) {
        if (error instanceof RollbackPathConflictError) {
          throw error;
        }
        // A newly completed content/provider copy can be verified at its
        // confined destination even when the provider URI cannot be hashed.
      }
      return copyingDigest;
    };

    const copyingState =
      await this.getQueueFileState(copyingPath, photo.size);
    let copyingDigest: string;
    if (copyingState === 'missing') {
      copyingDigest = await copyAndVerifySource();
    } else {
      if (options.acceptExistingCopying !== true) {
        throw new RollbackPathConflictError(
          'Existing copying-stage bytes are not safely replaceable'
        );
      }
      if (copyingState === 'unknown') {
        throw new RollbackPathConflictError(
          'Existing copying-stage bytes cannot be verified'
        );
      }

      const storedChecksum = this.getStoredQueueChecksum(photo);
      if (!storedChecksum) {
        throw new RollbackPathConflictError(
          'Existing copying-stage bytes have no durable source checksum'
        );
      }
      let sourceDigest: string;
      try {
        sourceDigest =
          await this.getQueueFileDigest(localPathFromUri(sourcePath));
      } catch {
        throw new Error(
          'Existing copying-stage bytes cannot be verified against the source'
        );
      }
      if (storedChecksum !== sourceDigest) {
        throw new RollbackPathConflictError(
          'The original photo conflicts with its durable source checksum'
        );
      }
      const expectedDigest = storedChecksum;
      copyingDigest = await this.getQueueFileDigest(copyingPath);
      if (
        copyingState !== 'valid' ||
        copyingDigest !== expectedDigest
      ) {
        await this.quarantineCopyingStage(photo, copyingPath);
        copyingDigest = await copyAndVerifySource(expectedDigest);
      }
    }

    const copyingStats = await RNFS.stat(copyingPath);
    const copiedSize = Number(copyingStats.size);
    if (!Number.isFinite(copiedSize) || copiedSize <= 0) {
      throw new Error('Copying-stage photo has no verifiable size');
    }
    checkpointMetadata = {
      ...checkpointMetadata,
      storageChecksum: copyingDigest,
    };
    if (photo.id) {
      await offlineDatabase.updatePhotoQueueItem(photo.id, {
        size: copiedSize,
        metadata: checkpointMetadata,
      });
    }
    photo.size = copiedSize;
    photo.metadata = checkpointMetadata;

    if (await RNFS.exists(readyPath)) {
      throw new RollbackPathConflictError(
        'Refusing to overwrite ready photo staging bytes'
      );
    }
    await RNFS.moveFile(copyingPath, readyPath);
    if (!(await this.isUsableQueueFile(readyPath, photo.size))) {
      throw new RollbackPathConflictError(
        'Ready photo staging copy could not be verified'
      );
    }
    if (await this.getQueueFileDigest(readyPath) !== copyingDigest) {
      throw new RollbackPathConflictError(
        'Ready photo staging checksum changed during publication'
      );
    }
    const readyMetadata = {
      ...checkpointMetadata,
      storageState: PHOTO_STORAGE_STATE_READY,
    };
    if (photo.id) {
      await offlineDatabase.updatePhotoQueueItem(photo.id, {
        localPath: readyPath,
        size: copiedSize,
        metadata: readyMetadata,
      });
    }
    photo.localPath = readyPath;
    photo.size = copiedSize;
    photo.metadata = readyMetadata;
    return {
      path: readyPath,
      size: copiedSize,
      digest: copyingDigest,
      metadata: readyMetadata,
    };
  }

  private async persistReadyStagingCheckpoint(
    photo: PhotoQueueItem,
    readyPath: string,
    size: number,
    digest: string
  ): Promise<{
    path: string;
    size: number;
    digest: string;
    metadata: PhotoQueueItem['metadata'];
  }> {
    const readyMetadata = {
      ...photo.metadata,
      storageState: PHOTO_STORAGE_STATE_READY,
      storageChecksum: digest,
    };
    if (photo.id) {
      await offlineDatabase.updatePhotoQueueItem(photo.id, {
        localPath: readyPath,
        size,
        metadata: readyMetadata,
      });
    }
    photo.localPath = readyPath;
    photo.size = size;
    photo.metadata = readyMetadata;
    return {
      path: readyPath,
      size,
      digest,
      metadata: readyMetadata,
    };
  }

  /**
   * Resume only a copy whose complete size and digest were durably checkpointed.
   * A partial or conflicting file is preserved for recovery and never promoted.
   */
  private async recoverCheckpointedCopyingPhoto(
    photo: PhotoQueueItem
  ): Promise<{
    publication: Awaited<ReturnType<
      PhotoQueueService['persistReadyStagingCheckpoint']
    >> | null;
    conflict: boolean;
    unknown: boolean;
  }> {
    if (photo.metadata?.storageState !== PHOTO_STORAGE_STATE_COPYING) {
      return { publication: null, conflict: false, unknown: false };
    }

    const storedChecksum = this.getStoredQueueChecksum(photo);
    const copyingPath = this.getStagingFilePath(photo.fileName, 'copying');
    const readyPath = this.getStagingFilePath(photo.fileName, 'ready');
    if (!storedChecksum || !copyingPath || !readyPath) {
      return { publication: null, conflict: false, unknown: false };
    }

    const validateCheckpointedPath = async (
      path: string
    ): Promise<'valid' | 'missing' | 'conflict' | 'unknown'> => {
      const state = await this.getQueueFileState(path, photo.size);
      if (state === 'missing' || state === 'unknown') {
        return state;
      }
      if (state !== 'valid') {
        return 'conflict';
      }
      try {
        return await this.getQueueFileDigest(path) === storedChecksum
          ? 'valid'
          : 'conflict';
      } catch {
        return 'unknown';
      }
    };

    const readyState = await validateCheckpointedPath(readyPath);
    if (readyState === 'valid') {
      return {
        publication: await this.persistReadyStagingCheckpoint(
          photo,
          readyPath,
          photo.size,
          storedChecksum
        ),
        conflict: false,
        unknown: false,
      };
    }
    if (readyState !== 'missing') {
      return {
        publication: null,
        conflict: readyState === 'conflict',
        unknown: readyState === 'unknown',
      };
    }

    const copyingState = await validateCheckpointedPath(copyingPath);
    if (copyingState !== 'valid') {
      return {
        publication: null,
        conflict: copyingState === 'conflict',
        unknown: copyingState === 'unknown',
      };
    }

    await RNFS.moveFile(copyingPath, readyPath);
    const publishedState = await validateCheckpointedPath(readyPath);
    if (publishedState !== 'valid') {
      return {
        publication: null,
        conflict: publishedState === 'conflict',
        unknown: publishedState === 'unknown',
      };
    }

    return {
      publication: await this.persistReadyStagingCheckpoint(
        photo,
        readyPath,
        photo.size,
        storedChecksum
      ),
      conflict: false,
      unknown: false,
    };
  }

  private async getIntegrityCheckedQueueFileState(
    photo: PhotoQueueItem,
    filePath: string,
    options: { requireStoredChecksum?: boolean } = {}
  ): Promise<'missing' | 'valid' | 'invalid' | 'unknown' | 'unverified'> {
    const state = await this.getQueueFileState(filePath, photo.size);
    if (state !== 'valid') {
      return state;
    }

    const storedChecksum = this.getStoredQueueChecksum(photo);
    if (!storedChecksum) {
      return options.requireStoredChecksum ? 'unverified' : 'valid';
    }

    try {
      return await this.getQueueFileDigest(filePath) === storedChecksum
        ? 'valid'
        : 'invalid';
    } catch {
      return 'unknown';
    }
  }

  /** Verify a temporary copy before publishing it; never alter the source. */
  private async publishQueueFileToV2(
    photo: Pick<PhotoQueueItem, 'id' | 'fileName' | 'size'>,
    sourcePath: string,
    options: {
      originalUri?: string;
      isContentUri?: boolean;
      acceptExistingCanonical?: boolean;
    } = {}
  ): Promise<string> {
    const canonicalPath = this.getCanonicalStoragePath(photo);
    if (!canonicalPath) {
      throw new Error('Queued photo filename is not a safe basename');
    }

    const temporaryFileName =
      `.migrating-${photo.id || 'untracked'}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    if (!this.isSafeQueueFileName(temporaryFileName)) {
      throw new Error('Queued photo staging filename is not a safe basename');
    }
    const temporaryPath =
      `${this.LOCAL_STORAGE_DIR}/${temporaryFileName}`;
    const normalizedSourcePath = localPathFromUri(sourcePath);
    let temporaryCreationAttempted = false;
    let temporaryMoved = false;

    try {
      const initialCanonicalState =
        await this.getQueueFileState(canonicalPath, photo.size);
      if (initialCanonicalState === 'valid') {
        if (options.acceptExistingCanonical === false) {
          throw new RollbackPathConflictError(
            'Refusing to share an existing v2 queue file'
          );
        }
        const [sourceDigest, canonicalDigest] = await Promise.all([
          this.getQueueFileDigest(normalizedSourcePath),
          this.getQueueFileDigest(canonicalPath),
        ]);
        if (sourceDigest === canonicalDigest) {
          return canonicalPath;
        }
        throw new RollbackPathConflictError(
          'Existing v2 queue file checksum conflicts with the queued photo'
        );
      }
      if (initialCanonicalState !== 'missing') {
        throw new RollbackPathConflictError(
          'Existing v2 queue destination is not safely replaceable'
        );
      }
      if (await RNFS.exists(temporaryPath)) {
        throw new RollbackPathConflictError(
          'Refusing to overwrite v2 queue staging bytes'
        );
      }

      temporaryCreationAttempted = true;
      await this.copyPhotoToQueueStorage(sourcePath, temporaryPath, options);
      if (!(await this.isUsableQueueFile(temporaryPath, photo.size))) {
        throw new Error('Copied photo failed size verification');
      }
      const [sourceDigest, temporaryDigest] = await Promise.all([
        this.getQueueFileDigest(normalizedSourcePath),
        this.getQueueFileDigest(temporaryPath),
      ]);
      if (sourceDigest !== temporaryDigest) {
        throw new Error('Copied photo failed checksum verification');
      }

      const canonicalState = await this.getQueueFileState(canonicalPath, photo.size);
      if (canonicalState === 'valid') {
        if (options.acceptExistingCanonical === false) {
          throw new RollbackPathConflictError(
            'Refusing to share an existing v2 queue file'
          );
        }
        const canonicalDigest =
          await this.getQueueFileDigest(canonicalPath);
        if (canonicalDigest === temporaryDigest) {
          return canonicalPath;
        }
        throw new RollbackPathConflictError(
          'Existing v2 queue file checksum conflicts with the queued photo'
        );
      }
      if (canonicalState !== 'missing') {
        throw new RollbackPathConflictError(
          'Existing v2 queue destination is not safely replaceable'
        );
      }

      await RNFS.moveFile(temporaryPath, canonicalPath);
      temporaryMoved = true;

      const publishedState = await this.getQueueFileState(canonicalPath, photo.size);
      if (publishedState !== 'valid') {
        throw new RollbackPathConflictError(
          'Published v2 queue file could not be verified'
        );
      }
      const publishedDigest = await this.getQueueFileDigest(canonicalPath);
      if (publishedDigest !== temporaryDigest) {
        throw new RollbackPathConflictError(
          'Published v2 queue file checksum changed during publication'
        );
      }

      return canonicalPath;
    } finally {
      if (temporaryCreationAttempted && !temporaryMoved) {
        const cleaned = await this.deleteConfinedQueueFile(
          temporaryPath,
          temporaryFileName
        );
        if (!cleaned && await RNFS.exists(temporaryPath)) {
          throw new Error(
            `Unable to clean temporary queued photo file: ${temporaryPath}`
          );
        }
      }
    }
  }

  /**
   * Atomically publish the v1-layout recovery source before a row can upload.
   * A conflicting canonical path is never moved or overwritten.
  */
  private async publishQueueFileToLegacy(
    photo: PhotoQueueItem,
    sourcePath: string,
    options: {
      originalUri?: string;
      isContentUri?: boolean;
      acceptExistingCanonical?: boolean;
      recoverAllocatedConflict?: boolean;
    } = {}
  ): Promise<string> {
    const legacyPath = this.getLegacyStoragePath(photo);
    if (!legacyPath) {
      throw new Error('Queued photo filename is not a safe basename');
    }

    const temporaryFileName =
      `.rollback-${photo.id || 'untracked'}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    if (!this.isSafeQueueFileName(temporaryFileName)) {
      throw new Error('Rollback staging filename is not a safe basename');
    }
    const temporaryPath = `${this.LEGACY_STORAGE_DIR}/${temporaryFileName}`;
    const normalizedSourcePath = localPathFromUri(sourcePath);
    let temporaryCreationAttempted = false;
    let temporaryMoved = false;

    try {
      let legacyState = await this.getQueueFileState(legacyPath, photo.size);
      if (
        options.acceptExistingCanonical === false &&
        legacyState !== 'missing'
      ) {
        throw new RollbackPathConflictError(
          'Refusing to overwrite an existing rollback copy'
        );
      }
      let sourceDigest: string | null = null;
      let legacyDigest: string | null = null;
      try {
        sourceDigest = await this.getQueueFileDigest(normalizedSourcePath);
      } catch {
        // Some external/content URIs cannot be hashed directly. The confined
        // temporary copy is still hashed before it can be published.
      }
      if (legacyState === 'valid') {
        try {
          legacyDigest = await this.getQueueFileDigest(legacyPath);
        } catch {
          // A destination that cannot be hashed remains fail-closed below.
        }
        if (
          sourceDigest &&
          legacyDigest &&
          sourceDigest === legacyDigest
        ) {
          return legacyPath;
        }
      }

      if (
        options.recoverAllocatedConflict === true &&
        photo.metadata?.rollbackFileAllocated === true &&
        (legacyState === 'invalid' || legacyState === 'valid')
      ) {
        const storedChecksum = this.getStoredQueueChecksum(photo);
        if (
          !storedChecksum ||
          !sourceDigest ||
          sourceDigest !== storedChecksum
        ) {
          throw new RollbackPathConflictError(
            'Rollback conflict cannot be tied to the allocated queued photo'
          );
        }
        await this.quarantineAllocatedLegacyConflict(photo, legacyPath);
        legacyState = 'missing';
        legacyDigest = null;
      }

      if (legacyState === 'unknown') {
        throw new RollbackPathConflictError(
          'Unable to verify a safe rollback destination'
        );
      }
      if (legacyState === 'invalid') {
        throw new RollbackPathConflictError(
          'Rollback destination is occupied by conflicting bytes'
        );
      }
      if (
        legacyState === 'valid' &&
        normalizedSourcePath === legacyPath
      ) {
        return legacyPath;
      }

      if (legacyState === 'valid') {
        if (
          sourceDigest &&
          legacyDigest &&
          sourceDigest === legacyDigest
        ) {
          return legacyPath;
        }
      }

      if (await RNFS.exists(temporaryPath)) {
        throw new Error('Refusing to overwrite rollback staging bytes');
      }

      temporaryCreationAttempted = true;
      await this.copyPhotoToQueueStorage(sourcePath, temporaryPath, options);
      if (!(await this.isUsableQueueFile(temporaryPath, photo.size))) {
        throw new Error('Rollback copy failed size verification');
      }
      const temporaryDigest = await this.getQueueFileDigest(temporaryPath);
      if (sourceDigest && sourceDigest !== temporaryDigest) {
        throw new Error('Rollback copy failed checksum verification');
      }

      const currentLegacyState =
        await this.getQueueFileState(legacyPath, photo.size);
      if (currentLegacyState === 'valid') {
        if (options.acceptExistingCanonical === false) {
          throw new RollbackPathConflictError(
            'Rollback destination became occupied during publication'
          );
        }
        legacyDigest = await this.getQueueFileDigest(legacyPath);
        if (legacyDigest === temporaryDigest) {
          return legacyPath;
        }
        throw new RollbackPathConflictError(
          'Rollback destination checksum conflicts with the queued photo'
        );
      }
      if (currentLegacyState !== 'missing') {
        throw new RollbackPathConflictError(
          'Rollback destination became unsafe during publication'
        );
      }

      await RNFS.moveFile(temporaryPath, legacyPath);
      temporaryMoved = true;
      const publishedState =
        await this.getQueueFileState(legacyPath, photo.size);
      if (publishedState !== 'valid') {
        throw new RollbackPathConflictError(
          'Published rollback copy could not be verified'
        );
      }
      const publishedDigest = await this.getQueueFileDigest(legacyPath);
      if (publishedDigest !== temporaryDigest) {
        throw new RollbackPathConflictError(
          'Published rollback copy checksum changed during publication'
        );
      }

      return legacyPath;
    } finally {
      if (temporaryCreationAttempted && !temporaryMoved) {
        const cleaned = await this.deleteConfinedQueueFile(
          temporaryPath,
          temporaryFileName,
          { allowLegacy: true }
        );
        if (!cleaned && await RNFS.exists(temporaryPath)) {
          throw new Error(
            `Unable to clean rollback staging file: ${temporaryPath}`
          );
        }
      }
    }
  }

  private async persistQueuePathBestEffort(
    photo: PhotoQueueItem,
    path: string
  ): Promise<boolean> {
    if (!photo.id || photo.localPath === path) {
      return false;
    }

    try {
      await offlineDatabase.updatePhotoQueueItem(photo.id, { localPath: path });
      return true;
    } catch (error) {
      console.warn(
        '[PhotoQueueService] Failed to persist verified queue path; retaining available bytes:',
        {
          photoId: photo.id,
          previousPath: photo.localPath,
          verifiedPath: path,
          error,
        }
      );
      return false;
    }
  }

  private async ensureQueuedPhotoFile(
    photo: PhotoQueueItem,
    allowOriginalRehydrate: boolean
  ): Promise<{
    exists: boolean;
    path: string;
    repaired: boolean;
    rehydrated: boolean;
    uncertain: boolean;
  }> {
    const canonicalPath = this.getCanonicalStoragePath(photo);
    const normalizedStoredPath = localPathFromUri(photo.localPath);
    const resolved = (
      exists: boolean,
      path: string,
      repaired = false,
      rehydrated = false,
      uncertain = false
    ) => ({ exists, path, repaired, rehydrated, uncertain });

    if (!canonicalPath) {
      const originalUri = allowOriginalRehydrate
        ? photo.metadata?.originalUri
        : undefined;
      if (typeof originalUri === 'string' && originalUri.length > 0) {
        const normalizedCandidate = localPathFromUri(originalUri);
        if (this.isReadableMigrationSource(
          normalizedCandidate,
          photo.fileName,
          true
        )) {
          const originalState =
            await this.getQueueFileState(normalizedCandidate, photo.size);
          if (originalState === 'valid') {
            return resolved(true, normalizedCandidate, false, true);
          }
          if (originalState === 'unknown') {
            return resolved(false, normalizedCandidate, false, false, true);
          }
        }
      }
      return resolved(false, normalizedStoredPath);
    }

    let sawUnknown = false;
    let stagingConflict = false;
    let readableSource: string | null = null;
    let sourceHasDurableIdentity = false;

    const checkpointRecovery =
      await this.recoverCheckpointedCopyingPhoto(photo);
    if (checkpointRecovery.publication) {
      readableSource = checkpointRecovery.publication.path;
      sourceHasDurableIdentity = true;
    }
    stagingConflict ||= checkpointRecovery.conflict;
    sawUnknown ||= checkpointRecovery.unknown;

    const recoverableStagingPath = this.getRecoverableStagingPath(photo);
    if (!readableSource && recoverableStagingPath) {
      const readyState = await this.getIntegrityCheckedQueueFileState(
        photo,
        recoverableStagingPath,
        {
          requireStoredChecksum:
            photo.metadata?.storageState === PHOTO_STORAGE_STATE_COPYING ||
            photo.metadata?.storageState === PHOTO_STORAGE_STATE_READY,
        }
      );
      if (readyState === 'valid') {
        readableSource = recoverableStagingPath;
        sourceHasDurableIdentity =
          Boolean(this.getStoredQueueChecksum(photo)) ||
          photo.metadata?.storageState === PHOTO_STORAGE_STATE_STAGING;
      } else if (readyState === 'unknown') {
        sawUnknown = true;
      } else if (
        readyState === 'invalid' ||
        readyState === 'unverified'
      ) {
        stagingConflict = true;
      }
    }

    const legacyPath = this.getLegacyStoragePath(photo);
    const storedChecksum = this.getStoredQueueChecksum(photo);
    const storageState = photo.metadata?.storageState;
    const finalPathRequiresChecksum =
      storageState === PHOTO_STORAGE_STATE_COPYING ||
      storageState === PHOTO_STORAGE_STATE_READY;

    const canonicalState = await this.getIntegrityCheckedQueueFileState(
      photo,
      canonicalPath,
      { requireStoredChecksum: finalPathRequiresChecksum }
    );
    if (!readableSource && canonicalState === 'valid') {
      readableSource = canonicalPath;
      sourceHasDurableIdentity =
        Boolean(storedChecksum) ||
        normalizedStoredPath === canonicalPath;
    } else if (canonicalState === 'unknown') {
      sawUnknown = true;
    } else if (
      canonicalState === 'invalid' &&
      Boolean(storedChecksum)
    ) {
      stagingConflict = true;
    }

    const readablePaths = Array.from(new Set(
      [normalizedStoredPath, legacyPath].filter(
        (candidate): candidate is string =>
          Boolean(candidate) && candidate !== canonicalPath
      )
    ));
    for (const candidate of readablePaths) {
      if (readableSource) {
        break;
      }
      if (!this.isReadableMigrationSource(candidate, photo.fileName)) {
        continue;
      }

      // Released v1 can repair a row created by v2 while leaving storageState
      // untouched and iOS may rotate the absolute container path again. Trust
      // the derived current-container copy only after the pre-copy checksum (or
      // a still-readable original) proves its identity.
      const hasReleasedV1CompletionMarker =
        storageState === PHOTO_STORAGE_STATE_COPYING &&
        Number(photo.size) > 0 &&
        normalizedStoredPath.startsWith('/') &&
        normalizedStoredPath.endsWith(
          `/photos_queue/${photo.fileName}`
        );
      const isReleasedV1LegacyCandidate =
        storageState === PHOTO_STORAGE_STATE_COPYING &&
        candidate === legacyPath &&
        (Boolean(storedChecksum) || hasReleasedV1CompletionMarker);
      let candidateState = await this.getIntegrityCheckedQueueFileState(
        photo,
        candidate,
        {
          requireStoredChecksum:
            finalPathRequiresChecksum &&
            !(isReleasedV1LegacyCandidate && !storedChecksum),
        }
      );
      let v1PointerDigestVerified = false;
      if (
        candidateState === 'valid' &&
        isReleasedV1LegacyCandidate &&
        !storedChecksum
      ) {
        const originalUri = photo.metadata?.originalUri;
        const normalizedOriginalUri = localPathFromUri(originalUri);
        if (
          typeof originalUri !== 'string' ||
          originalUri.length === 0 ||
          normalizedOriginalUri === candidate ||
          !this.isReadableMigrationSource(
            normalizedOriginalUri,
            photo.fileName,
            true
          )
        ) {
          candidateState = 'unverified';
        } else {
          const originalState =
            await this.getQueueFileState(normalizedOriginalUri, photo.size);
          if (originalState === 'valid') {
            try {
              v1PointerDigestVerified =
                await this.getQueueFileDigest(candidate) ===
                await this.getQueueFileDigest(normalizedOriginalUri);
              candidateState =
                v1PointerDigestVerified ? 'valid' : 'invalid';
            } catch {
              candidateState = 'unknown';
            }
          } else {
            candidateState =
              originalState === 'unknown' ? 'unknown' : 'unverified';
          }
        }
      }
      if (candidateState === 'valid') {
        readableSource = candidate;
        sourceHasDurableIdentity =
          Boolean(storedChecksum) ||
          v1PointerDigestVerified ||
          (
            storageState === undefined &&
            normalizedStoredPath === candidate
          );
        break;
      }
      if (candidateState === 'unknown') {
        sawUnknown = true;
      } else if (
        candidateState === 'invalid' ||
        candidateState === 'unverified'
      ) {
        stagingConflict = true;
      }
    }

    if (readableSource) {
      // Old committed rows have no persisted digest. Preserve the earlier
      // fail-closed comparison for those rows, but never let a mutable picker
      // URI override a checkpointed READY file or v1's row-addressed copy.
      const comparisonOriginalUri = photo.metadata?.originalUri;
      if (
        !sourceHasDurableIdentity &&
        typeof comparisonOriginalUri === 'string' &&
        comparisonOriginalUri.length > 0
      ) {
        const normalizedOriginalUri =
          localPathFromUri(comparisonOriginalUri);
        if (
          normalizedOriginalUri !== readableSource &&
          this.isReadableMigrationSource(
            normalizedOriginalUri,
            photo.fileName,
            true
          ) &&
          await this.getQueueFileState(normalizedOriginalUri, photo.size) ===
            'valid'
        ) {
          try {
            const [sourceDigest, originalDigest] = await Promise.all([
              this.getQueueFileDigest(readableSource),
              this.getQueueFileDigest(normalizedOriginalUri),
            ]);
            if (sourceDigest !== originalDigest) {
              throw new RollbackPathConflictError(
                'Queued photo bytes conflict with the readable original'
              );
            }
          } catch (error) {
            if (error instanceof RollbackPathConflictError) {
              throw error;
            }
            // An optional original that cannot be hashed must not strand a
            // committed canonical/legacy queue copy.
          }
        }
      }

      const rollbackSource = await this.publishQueueFileToLegacy(
        photo,
        readableSource,
        { recoverAllocatedConflict: true }
      );
      try {
        await this.publishQueueFileToV2(photo, rollbackSource);
        const pathRepaired =
          await this.persistQueuePathBestEffort(photo, canonicalPath);
        return resolved(true, canonicalPath, pathRepaired);
      } catch (error) {
        if (error instanceof RollbackPathConflictError) {
          throw error;
        }
        // Keep v1 authoritative and retry expansion later.
        console.warn('[PhotoQueueService] Failed to copy queued photo into v2 storage; retaining readable source:', {
          photoId: photo.id,
          sourcePath: readableSource,
          canonicalPath,
          error,
        });
        const pathRepaired =
          await this.persistQueuePathBestEffort(photo, rollbackSource);
        return resolved(true, rollbackSource, pathRepaired);
      }
    }

    const originalUri = photo.metadata?.originalUri;
    if (
      allowOriginalRehydrate &&
      typeof originalUri === 'string' &&
      originalUri.length > 0
    ) {
      const normalizedOriginalUri = localPathFromUri(originalUri);
      if (this.isReadableMigrationSource(
        normalizedOriginalUri,
        photo.fileName,
        true
      )) {
        const originalState =
          await this.getQueueFileState(normalizedOriginalUri, photo.size);
        if (
          originalState === 'valid' ||
          originalState === 'unknown' ||
          originalUri.startsWith('content://')
        ) {
          try {
            const readyPublication =
              await this.publishEphemeralSourceToReady(
                photo,
                normalizedOriginalUri,
                {
                  originalUri,
                  isContentUri: originalUri.startsWith('content://'),
                  acceptExistingCopying: true,
                }
              );
            const rollbackSource = await this.publishQueueFileToLegacy(
              photo,
              readyPublication.path,
              { recoverAllocatedConflict: true }
            );
            try {
              await this.publishQueueFileToV2(photo, rollbackSource);
              const pathRepaired =
                await this.persistQueuePathBestEffort(photo, canonicalPath);
              return resolved(
                true,
                canonicalPath,
                pathRepaired,
                true
              );
            } catch (error) {
              if (error instanceof RollbackPathConflictError) {
                throw error;
              }
              const pathRepaired =
                await this.persistQueuePathBestEffort(photo, rollbackSource);
              return resolved(
                true,
                rollbackSource,
                pathRepaired,
                true
              );
            }
          } catch (error) {
            if (error instanceof RollbackPathConflictError) {
              stagingConflict = true;
            } else {
              console.warn(
                '[PhotoQueueService] Original photo could not be republished; retaining every recovery source:',
                { photoId: photo.id, originalUri, error }
              );
              sawUnknown = true;
            }
          }
        }
      }
    }

    if (
      stagingConflict ||
      sawUnknown ||
      this.isUnverifiedStorageCopyingPhoto(photo) ||
      photo.metadata?.storageState === PHOTO_STORAGE_STATE_READY
    ) {
      return resolved(
        false,
        this.getStagingFilePath(photo.fileName, 'copying') ||
          recoverableStagingPath ||
          canonicalPath,
        false,
        false,
        true
      );
    }

    return resolved(false, canonicalPath);
  }

  private async promoteRecoveredStagingPhoto(
    photo: PhotoQueueItem,
    resolved: {
      exists: boolean;
      path: string;
    },
    options: { allowLegacyRecovery?: boolean } = {}
  ): Promise<boolean> {
    const canonicalPath = this.getCanonicalStoragePath(photo);
    const legacyPath = this.getLegacyStoragePath(photo);
    const isVerifiedPublicationPath =
      resolved.path === canonicalPath ||
      (
        Boolean(options.allowLegacyRecovery) &&
        resolved.path === legacyPath
      );
    if (
      !photo.id ||
      !this.isStorageStagingPhoto(photo) ||
      !resolved.exists ||
      !isVerifiedPublicationPath
    ) {
      return false;
    }

    const stats = await RNFS.stat(resolved.path);
    const recoveredSize = Number(stats.size);
    if (!Number.isFinite(recoveredSize) || recoveredSize <= 0) {
      return false;
    }
    const recoveredMetadata = { ...photo.metadata };
    delete recoveredMetadata.storageState;
    delete recoveredMetadata.rollbackFileAllocated;
    await offlineDatabase.updatePhotoQueueItem(photo.id, {
      localPath: resolved.path,
      size: recoveredSize,
      status: 'pending',
      retryCount: 0,
      errorMessage: null,
      metadata: recoveredMetadata,
    });
    return true;
  }

  private async repairStoredQueuePaths(): Promise<PhotoQueueRepairResult> {
    const result: PhotoQueueRepairResult = {
      verified: 0,
      pathRepaired: 0,
      rehydrated: 0,
      missing: 0,
      missingPhotoIds: [],
    };
    const photos = await this.getQueuedPhotos({});

    for (const photo of photos.filter(candidate => candidate.status !== 'uploaded')) {
      let resolved: Awaited<ReturnType<
        PhotoQueueService['ensureQueuedPhotoFile']
      >>;
      try {
        resolved = await this.ensureQueuedPhotoFile(photo, true);
      } catch (error) {
        // Lifecycle/environment transitions are not storage-repair failures.
        // Preserve their cancellation semantics instead of quarantining a row
        // from an environment that is no longer active.
        this.captureEnvironmentKey();
        console.warn(
          '[PhotoQueueService] Unable to prepare rollback-safe queued photo bytes; retaining the row for explicit recovery:',
          { photoId: photo.id, error }
        );
        if (photo.id && !this.isStorageStagingPhoto(photo)) {
          await offlineDatabase.updatePhotoQueueItem(photo.id, {
            status: 'failed',
            retryCount: Math.max(photo.retryCount, this.MAX_RETRIES),
            errorMessage: PHOTO_ROLLBACK_COPY_ERROR,
          });
        }
        continue;
      }
      if (resolved.exists) {
        await this.promoteRecoveredStagingPhoto(photo, resolved);
        result.verified += 1;
        if (resolved.repaired) result.pathRepaired += 1;
        if (resolved.rehydrated) result.rehydrated += 1;
        continue;
      }

      if (resolved.uncertain) {
        console.warn(
          '[PhotoQueueService] Queue file state is temporarily unverifiable; retaining retry state:',
          { photoId: photo.id, path: resolved.path }
        );
        continue;
      }

      result.missing += 1;
      if (photo.id) {
        result.missingPhotoIds.push(photo.id);
        await offlineDatabase.updatePhotoQueueItem(photo.id, {
          status: 'failed',
          errorMessage: PHOTO_FILE_MISSING_ERROR,
        });
      }
    }

    return result;
  }

  /**
   * Clean up expired items and orphaned files
   */
  private async cleanupExpiredItems(): Promise<void> {
    try {
      // Get uploaded photos older than 24 hours for cleanup
      const uploadedPhotos = await this.getQueuedPhotos({ status: 'uploaded' });
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      for (const photo of uploadedPhotos) {
        const uploadedAt = photo.lastAttemptAt || photo.createdAt;
        if (uploadedAt < oneDayAgo && photo.id) {
          // Automatic cleanup owns only active-v2 bytes in the expand release.
          if (this.isPathInActiveStorage(photo.localPath, photo.fileName)) {
            await this.deleteConfinedQueueFile(photo.localPath, photo.fileName);
          }
          await offlineDatabase.removePhotoFromQueue(photo.id);
        }
      }

      // Expand release: do not enumerate or delete unreferenced v2 files.
      // A process termination can leave the only recoverable copy in a
      // publication temp file. Contract cleanup requires a durable completion
      // marker and ships separately after upgrade/rollback validation.
    } catch (error) {
      console.warn('[PhotoQueueService] Cleanup failed:', error);
    }
  }

  /**
   * Cleanup resources
   */
  async destroy(): Promise<void> {
    if (this.destructionPromise) {
      return this.destructionPromise;
    }

    const pendingInitialization = this.initializationPromise;
    this.lifecycleGeneration += 1;
    this.uploadInProgress = false;
    this.uploadRerunRequested = false;
    this.initialized = false;

    try {
      if (this.networkListener) {
        this.networkListener();
        this.networkListener = null;
      }
    } catch (error) {
      console.warn('[PhotoQueueService] Error during destroy:', error);
    }

    const destructionPromise = (async () => {
      if (pendingInitialization) {
        await pendingInitialization.catch(() => undefined);
      }
      this.initializationPromise = null;
      this.activeEnvironmentKey = null;
    })();
    this.destructionPromise = destructionPromise;

    try {
      await destructionPromise;
    } finally {
      if (this.destructionPromise === destructionPromise) {
        this.destructionPromise = null;
      }
    }
  }

  /**
   * Copy source photo into queue storage with retries/fallbacks
   */
  private async copyPhotoToQueueStorage(
    sourceUri: string,
    destinationPath: string,
    options: { originalUri?: string; isContentUri?: boolean } = {}
  ): Promise<void> {
    const { originalUri, isContentUri } = options;

    await this.ensureStorageDirectory();
    const destinationFileName = this.getDirectChildFileName(
      this.LOCAL_STORAGE_DIR,
      destinationPath
    );
    const legacyDestinationFileName = this.getDirectChildFileName(
      this.LEGACY_STORAGE_DIR,
      destinationPath
    );
    if (!destinationFileName && !legacyDestinationFileName) {
      throw new Error('Refusing to copy a queued photo outside owned storage');
    }

    let copySucceeded = false;
    try {
      await RNFS.copyFile(sourceUri, destinationPath);
      copySucceeded = true;
    } catch (copyError) {
      const fallbackSource = originalUri || sourceUri;

      if (isContentUri || fallbackSource.startsWith('content://')) {
        try {
          const data = await RNFS.readFile(fallbackSource, 'base64');
          await RNFS.writeFile(destinationPath, data, 'base64');
          copySucceeded = true;
        } catch (fallbackError) {
          console.error('[PhotoQueueService] Failed to copy content URI photo to queue storage (fallback failed)', fallbackError, {
            originalUri: fallbackSource,
            destinationPath,
          });
          throw fallbackError;
        }
      } else {
        console.error('[PhotoQueueService] Failed to copy photo to queue storage', copyError, {
          sourceUri,
          destinationPath,
        });
        throw copyError;
      }
    }

    if (!copySucceeded || !(await RNFS.exists(destinationPath))) {
      console.error('[PhotoQueueService] Photo copy verification failed - storage file missing', {
        sourceUri,
        destinationPath,
      });
      throw new Error('Failed to copy photo to queue storage');
    }
  }
}

// Export singleton instance
export const photoQueueService = new PhotoQueueService();
